/**
 * All localStorage for the standalone SDM Auth0 device-flow grant.
 *
 * The rotating refresh token (`sdm_session`) is the credential. The UUID and
 * startedAt stamp identify that login for metrics; they are created with the
 * first token write, kept across rotation, and cleared with the token.
 */

export const SDM_DEVICE_REFRESH_TOKEN_KEY = 'sdm_session';
export const SDM_AUTH_SESSION_ID_KEY = 'sdm_auth_session_id';
export const SDM_AUTH_SESSION_STARTED_AT_KEY = 'sdm_auth_session_started_at';

export interface SdmAuthSession {
  id: string;
  startedAtMs: number;
  ageSeconds: number;
}

export type SdmAuthSessionClearReason = 'logout' | 'refresh_failed';

export interface SdmAuthSessionClearedEvent {
  session: SdmAuthSession;
  reason: SdmAuthSessionClearReason;
}

const clearedListeners = new Set<(event: SdmAuthSessionClearedEvent) => void>();

/** Same-tab: storage events do not fire in the document that called removeItem. */
export function subscribeSdmAuthSessionCleared(
  listener: (event: SdmAuthSessionClearedEvent) => void,
): () => void {
  clearedListeners.add(listener);
  return () => {
    clearedListeners.delete(listener);
  };
}

function readStoredSession(nowMs: number): SdmAuthSession | null {
  const id = localStorage.getItem(SDM_AUTH_SESSION_ID_KEY);
  const startedAtMs = Number(localStorage.getItem(SDM_AUTH_SESSION_STARTED_AT_KEY));
  if (!id || !Number.isFinite(startedAtMs)) {
    return null;
  }
  return {
    id,
    startedAtMs,
    ageSeconds: Math.max(0, (nowMs - startedAtMs) / 1000),
  };
}

/** Create the login stamp if missing. Do nothing when a session is already stored. */
export function ensureSdmAuthSession(nowMs = Date.now()): SdmAuthSession {
  const existing = readStoredSession(nowMs);
  if (existing) {
    return existing;
  }

  const created: SdmAuthSession = {
    id: crypto.randomUUID(),
    startedAtMs: nowMs,
    ageSeconds: 0,
  };
  localStorage.setItem(SDM_AUTH_SESSION_ID_KEY, created.id);
  localStorage.setItem(SDM_AUTH_SESSION_STARTED_AT_KEY, String(created.startedAtMs));
  return created;
}

export function getSdmRefreshToken(): string | null {
  return localStorage.getItem(SDM_DEVICE_REFRESH_TOKEN_KEY);
}

/** Write the rotating refresh token and stamp the login if this is a new grant. */
export function storeSdmRefreshToken(token: string, nowMs = Date.now()): void {
  localStorage.setItem(SDM_DEVICE_REFRESH_TOKEN_KEY, token);
  ensureSdmAuthSession(nowMs);
}

export function getSdmAuthSession(nowMs = Date.now()): SdmAuthSession | null {
  return readStoredSession(nowMs);
}

/**
 * Session to attach to a heartbeat. Backfills a stamp for kiosks that already
 * have a refresh token from before this metric existed.
 */
export function getSdmAuthSessionForHeartbeat(nowMs = Date.now()): SdmAuthSession | null {
  if (getSdmRefreshToken()) {
    return ensureSdmAuthSession(nowMs);
  }
  return getSdmAuthSession(nowMs);
}

/** Drop the refresh token and the login stamp together. */
export function clearSdmAuthSession(
  reason: SdmAuthSessionClearReason = 'logout',
  nowMs = Date.now(),
): void {
  const session = readStoredSession(nowMs);
  localStorage.removeItem(SDM_DEVICE_REFRESH_TOKEN_KEY);
  localStorage.removeItem(SDM_AUTH_SESSION_ID_KEY);
  localStorage.removeItem(SDM_AUTH_SESSION_STARTED_AT_KEY);
  if (!session) {
    return;
  }
  const event: SdmAuthSessionClearedEvent = { session, reason };
  for (const listener of clearedListeners) {
    listener(event);
  }
}
